import clsx from 'clsx';
import { CSSProperties, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';

import { IQueryParams, ITrackEventProps } from 'typescriptDefs';
import { LocaleNavbarTab, LocaleNavbarTabLink, LocaleNavbarTabSection } from 'typescriptDefs/layout';

import { useMeasureInnerHeight } from 'helpers';
import { generalAppsFlyerIds } from 'helpers/getAfTracking';

import { spaceValues } from 'styles/tw-themes/spaceValues';

import { Accordion } from '../Accordion';
import { P } from '../Typography';
import { FeaturedLink } from './FeaturedLink';
import { LoginAndRegistration } from './LoginAndRegistration';
import { NavDescription, NavLink, SectionTitle } from './NavTypography';

type Props = {
  closeMobileMenu: () => void;
  darkBg: boolean;
  navItemsOverride?: { text: string; url: string }[];
  tabs: LocaleNavbarTab[];
  queryParams: IQueryParams;
  trackEventProps: ITrackEventProps;
  trackLinkClicked: (label: string) => void;
};

export const MobileMenu = ({
  closeMobileMenu,
  darkBg,
  navItemsOverride,
  tabs,
  queryParams,
  trackEventProps,
  trackLinkClicked,
}: Props) => {
  const [openTabIndex, setOpenTabIndex] = useState<number | null>(null);
  const innerHeight = useMeasureInnerHeight();

  return (
    <>
      <div
        className={twMerge(
          clsx(
            'absolute z-nav block w-full overflow-scroll border-t border-grey-100 bg-white pb-80 pt-8 lg:hidden',
            'top-navMobileHeight h-[var(--navbar-mobile-maxHeight)]',
            darkBg && 'border-black bg-primary-500 text-white',
          ),
        )}
        style={
          { '--navbar-mobile-maxHeight': `calc(${innerHeight}px - ${spaceValues.navMobileHeight})` } as CSSProperties
        }
      >
        {navItemsOverride ? (
          <Container className="px-32 xs:px-0">
            <div className="mb-40 ml-8 mt-24 flex flex-col gap-24">
              {navItemsOverride.map((link) => (
                <NavLink
                  key={link.text}
                  title
                  className={twMerge(
                    clsx(
                      'w-full [&>div>div]:border-t-[0.5px] [&>div>div]:border-grey-100 [&>div>div]:bg-primary-50 [&>div]:mt-0',
                      '[&_button]:mx-auto [&_button]:px-32 [&_button]:xs:w-[540px] [&_button]:xs:px-0 [&_button]:sm:w-[720px] [&_button]:md:w-[960px] [&_button]:lg:w-[1140px]',
                      darkBg &&
                        '[&>div>div]:border-grey-200 [&>div>div]:border-opacity-30 [&>div>div]:bg-black [&>div>div]:bg-opacity-15',
                    ),
                  )}
                  darkBg={darkBg}
                  item={{ title: link.text, url: link.url }}
                  onClick={() => {
                    trackLinkClicked(link.text);
                    closeMobileMenu();
                  }}
                />
              ))}
            </div>
          </Container>
        ) : (
          tabs.map((tab, tabIndex) => (
            <>
              <Accordion
                singleExpandControl={{
                  isOpen: openTabIndex === tabIndex,
                  onToggle: () => setOpenTabIndex(openTabIndex === tabIndex ? null : tabIndex),
                }}
                key={tab.title}
                className={twMerge(
                  clsx(
                    'w-full [&>div>div]:border-t-[0.5px] [&>div>div]:border-grey-100 [&>div>div]:bg-primary-50 [&>div]:mt-0',
                    '[&_button]:mx-auto [&_button]:px-32 [&_button]:xs:w-[540px] [&_button]:xs:px-0 [&_button]:sm:w-[720px] [&_button]:md:w-[960px] [&_button]:lg:w-[1140px]',
                    darkBg &&
                      '[&>div>div]:border-grey-200 [&>div>div]:border-opacity-30 [&>div>div]:bg-black [&>div>div]:bg-opacity-15',
                  ),
                )}
                title={
                  <P className={twMerge(clsx('py-16 font-medium text-primary-500', darkBg && 'text-white'))}>
                    {tab.title}
                  </P>
                }
              >
                <Container className="px-32 xs:px-0">
                  <div className="mb-40 ml-8 mt-24 flex flex-col gap-24">
                    {tab.content.map((section, i) => (
                      <>
                        <div key={section.title}>
                          {section._type === 'siteNavSection' ? (
                            <NavbarTabSection darkBg={darkBg} section={section} trackLinkClicked={trackLinkClicked} />
                          ) : (
                            <NavbarTabLink darkBg={darkBg} link={section} trackLinkClicked={trackLinkClicked} />
                          )}
                        </div>
                        <div
                          key={`tabSectionBorder-${i}`}
                          className={twMerge(
                            clsx('h-[0.5px] w-full bg-grey-100 last:hidden', darkBg && 'bg-opacity-30'),
                          )}
                        />
                      </>
                    ))}
                    {tab.featured && (
                      <FeaturedLink darkBg={darkBg} featured={tab.featured} trackLinkClicked={trackLinkClicked} />
                    )}
                  </div>
                </Container>
              </Accordion>
              <div
                key={`tabBorder-${tabIndex}`}
                className={twMerge(
                  clsx('h-[0.5px] w-full bg-grey-200 bg-opacity-30', openTabIndex === tabIndex && 'hidden'),
                )}
              />
            </>
          ))
        )}
      </div>
      <div
        className={twMerge(
          clsx(
            'fixed bottom-0 z-nav flex w-full items-center justify-center border-t border-grey-100 bg-inherit p-16 bg-white',
            darkBg && 'border-black',
          ),
        )}
      >
        <LoginAndRegistration
          darkBg={darkBg}
          buttonLocation="Navbar mobile"
          queryParams={{
            hash: queryParams.hash,
            search: {
              ...queryParams.search,
              af_adset_id: generalAppsFlyerIds.NAV_MOBILE,
            },
          }}
          trackEventProps={trackEventProps}
        />
      </div>
    </>
  );
};

const NavbarTabSection = ({
  darkBg,
  section,
  trackLinkClicked,
}: {
  darkBg: boolean;
  section: LocaleNavbarTabSection;
  trackLinkClicked: (label: string) => void;
}) => {
  return (
    <>
      <SectionTitle className="mb-4" darkBg={darkBg}>
        {section.title}
      </SectionTitle>
      {section.description && (
        <NavDescription className="mb-16" darkBg={darkBg}>
          {section.description}
        </NavDescription>
      )}
      <div className="ml-24 flex flex-col gap-16">
        {section.links.map((item) => (
          <NavLink key={item.title} item={item} darkBg={darkBg} onClick={() => trackLinkClicked(item.title)} />
        ))}
      </div>
    </>
  );
};

const NavbarTabLink = ({
  darkBg,
  link,
  trackLinkClicked,
}: {
  darkBg: boolean;
  link: LocaleNavbarTabLink;
  trackLinkClicked: (label: string) => void;
}) => {
  return (
    <>
      <NavLink title className="mb-4" darkBg={darkBg} item={link} onClick={() => trackLinkClicked(link.title)} />
      {link.description && <NavDescription darkBg={darkBg}>{link.description}</NavDescription>}
    </>
  );
};
